/**
 * VistoeraTASK-00638 / 00671 / 00672 — Member auth matrix.
 * Distinguishes Team Feature Switch vs Member Grant vs final effective.
 */
import { Empty, Select, Table, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useMemo } from 'react'
import { grantKey, type MatrixChannelCell, type MatrixRow } from './authMatrixDraft'
import {
  memberDecisionLabel,
  workspaceAllowedLabel,
  type MemberDecision,
} from './memberAuthMatrixDraft'
import './auth-matrix-table.css'

/** Primary member UX: INHERIT | DENY. ALLOW kept for historical rows / compat. */
const DECISION_OPTIONS: Array<{ value: MemberDecision; label: string }> = [
  { value: 'INHERIT', label: '继承团队' },
  { value: 'DENY', label: '禁止' },
  { value: 'ALLOW', label: '允许' },
]

export type MemberAuthMatrixTableProps = {
  rows: MatrixRow[]
  draft: Record<string, MemberDecision>
  baseline: Record<string, MemberDecision>
  workspaceEffective: Record<string, boolean>
  memberEffective: Record<string, boolean>
  selectedNodeId: string | null
  onSelectNode: (nodeId: string) => void
  onDecisionChange: (kind: 'node' | 'channel', subjectId: string, decision: MemberDecision) => void
  /** TASK-00671 — Owner can set Team Feature Switch (INHERIT|DENY). */
  canManageTeamSwitch?: boolean
  onTeamSwitchChange?: (kind: 'node' | 'channel', subjectId: string, decision: 'INHERIT' | 'DENY') => void
  teamSwitchSavingKey?: string | null
  loading?: boolean
  readOnly?: boolean
}

function teamLabel(closed: boolean): string {
  return closed ? '关闭' : '开启'
}

function metaLine(
  teamClosed: boolean,
  memberDec: MemberDecision,
  finalAllowed: boolean | undefined,
) {
  return (
    <div className="amx-ws-meta">
      <span>团队：{teamLabel(teamClosed)}</span>
      <span>成员：{memberDecisionLabel(memberDec)}</span>
      <span>最终：{workspaceAllowedLabel(finalAllowed)}</span>
    </div>
  )
}

function memberOptions(canAllow: boolean, current: MemberDecision) {
  return DECISION_OPTIONS.filter((o) => o.value !== 'ALLOW' || canAllow || current === 'ALLOW').map((o) =>
    o.value === 'ALLOW' && !canAllow
      ? { ...o, disabled: true, label: '允许（不可突破上层）' }
      : o,
  )
}

function ChannelPanel({
  row,
  draft,
  baseline,
  workspaceEffective,
  memberEffective,
  onDecisionChange,
  canManageTeamSwitch,
  onTeamSwitchChange,
  teamSwitchSavingKey,
  readOnly,
}: {
  row: MatrixRow
  draft: Record<string, MemberDecision>
  baseline: Record<string, MemberDecision>
  workspaceEffective: Record<string, boolean>
  memberEffective: Record<string, boolean>
  onDecisionChange: (kind: 'node' | 'channel', subjectId: string, decision: MemberDecision) => void
  canManageTeamSwitch?: boolean
  onTeamSwitchChange?: (kind: 'node' | 'channel', subjectId: string, decision: 'INHERIT' | 'DENY') => void
  teamSwitchSavingKey?: string | null
  readOnly?: boolean
}) {
  const channels = row.channels || []
  if (channels.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="当前节点暂无已映射的执行通道"
        style={{ margin: '24px 0' }}
      />
    )
  }

  return (
    <div className="amx-channel-panel-scroll">
      <div className="amx-ws-channel-list">
        {channels.map((ch: MatrixChannelCell) => {
          const key = grantKey('channel', ch.subject_id)
          const decision = draft[key] ?? 'INHERIT'
          const dirty = (baseline[key] ?? 'INHERIT') !== decision
          const wsOk = workspaceEffective[key] !== false
          const teamClosed = Boolean((ch as { team_closed?: boolean }).team_closed)
          const teamDec = ((ch as { team_decision?: string }).team_decision || 'INHERIT') as 'INHERIT' | 'DENY'
          const globalOk = Boolean(ch.globally_available)
          const canAllow = wsOk && !teamClosed && globalOk
          const label = ch.display_name || ch.subject_id
          const switchKey = `channel:${ch.subject_id}`
          return (
            <div key={ch.subject_id} className={`amx-ws-channel-row${dirty ? ' is-dirty' : ''}`}>
              <div className="amx-ws-channel-main">
                <span className="amx-channel-chip-label" title={label}>
                  {label}
                </span>
                {!globalOk ? <Tag>全站已关闭</Tag> : null}
                {!wsOk ? <Tag color="red">Workspace 禁止</Tag> : null}
                {teamClosed ? <Tag color="gold">团队已关闭</Tag> : null}
                {dirty ? <Tag color="orange">未保存</Tag> : null}
              </div>
              {metaLine(teamClosed, decision, memberEffective[key])}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                {canManageTeamSwitch ? (
                  <Select
                    size="small"
                    style={{ width: 128 }}
                    value={teamDec}
                    disabled={teamSwitchSavingKey === switchKey}
                    options={[
                      { value: 'INHERIT', label: '团队：开启' },
                      { value: 'DENY', label: '团队：关闭' },
                    ]}
                    onChange={(v) => onTeamSwitchChange?.('channel', ch.subject_id, v)}
                    getPopupContainer={() => document.body}
                    aria-label="团队功能开关"
                  />
                ) : (
                  <Tag>{`团队：${teamLabel(teamClosed)}`}</Tag>
                )}
                <Select
                  size="small"
                  style={{ width: 120 }}
                  value={decision}
                  disabled={readOnly || !globalOk}
                  options={memberOptions(canAllow, decision)}
                  onChange={(v) => onDecisionChange('channel', ch.subject_id, v)}
                  getPopupContainer={() => document.body}
                  aria-label="成员独立授权"
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default function MemberAuthMatrixTable({
  rows,
  draft,
  baseline,
  workspaceEffective,
  memberEffective,
  selectedNodeId,
  onSelectNode,
  onDecisionChange,
  canManageTeamSwitch,
  onTeamSwitchChange,
  teamSwitchSavingKey,
  loading,
  readOnly,
}: MemberAuthMatrixTableProps) {
  const selectedRow = useMemo(
    () => rows.find((r) => r.node.subject_id === selectedNodeId) ?? null,
    [rows, selectedNodeId],
  )

  const columns: ColumnsType<MatrixRow> = useMemo(
    () => [
      {
        title: '节点类型',
        key: 'node',
        width: 168,
        ellipsis: true,
        render: (_: unknown, row) => {
          const selected = row.node.subject_id === selectedNodeId
          const label = (row.node.display_name || '').trim() || row.node.subject_id
          const teamClosed = Boolean((row.node as { team_closed?: boolean }).team_closed)
          return (
            <button
              type="button"
              className={`amx-node-select${selected ? ' amx-node-select-active' : ''}`}
              onClick={() => onSelectNode(row.node.subject_id)}
              title={label}
              data-node-id={row.node.subject_id}
            >
              <span className="amx-node-name">{label}</span>
              {teamClosed ? (
                <Tag color="gold" style={{ marginInlineStart: 4 }}>
                  团队已关闭
                </Tag>
              ) : null}
              {!row.node.globally_available ? (
                <Typography.Text type="secondary" className="amx-node-hint">
                  全站已关闭
                </Typography.Text>
              ) : null}
            </button>
          )
        },
      },
      {
        title: '团队 / 成员授权',
        key: 'node_decision',
        width: 280,
        render: (_: unknown, row) => {
          const key = grantKey('node', row.node.subject_id)
          const decision = draft[key] ?? 'INHERIT'
          const dirty = (baseline[key] ?? 'INHERIT') !== decision
          const wsOk = workspaceEffective[key] !== false
          const teamClosed = Boolean((row.node as { team_closed?: boolean }).team_closed)
          const teamDec = ((row.node as { team_decision?: string }).team_decision || 'INHERIT') as
            | 'INHERIT'
            | 'DENY'
          const globalOk = Boolean(row.node.globally_available)
          const canAllow = wsOk && !teamClosed && globalOk
          const switchKey = `node:${row.node.subject_id}`
          return (
            <div className="amx-ws-node-cell" onClick={(e) => e.stopPropagation()}>
              {metaLine(teamClosed, decision, memberEffective[key])}
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                {canManageTeamSwitch ? (
                  <Select
                    size="small"
                    style={{ width: 128 }}
                    value={teamDec}
                    disabled={teamSwitchSavingKey === switchKey}
                    options={[
                      { value: 'INHERIT', label: '团队：开启' },
                      { value: 'DENY', label: '团队：关闭' },
                    ]}
                    onChange={(v) => onTeamSwitchChange?.('node', row.node.subject_id, v)}
                    getPopupContainer={() => document.body}
                    aria-label="团队功能开关"
                  />
                ) : (
                  <Tag>{`团队：${teamLabel(teamClosed)}`}</Tag>
                )}
                <Select
                  size="small"
                  style={{ width: 120 }}
                  value={decision}
                  disabled={readOnly || !globalOk}
                  options={memberOptions(canAllow, decision)}
                  onChange={(v) => onDecisionChange('node', row.node.subject_id, v)}
                  getPopupContainer={() => document.body}
                  aria-label="成员独立授权"
                />
                {dirty ? <Tag color="orange">未保存</Tag> : null}
              </div>
              {!wsOk ? (
                <Tooltip title="Workspace 明确禁止时，成员 ALLOW 不会生效">
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    成员允许无法突破 Workspace 禁止
                  </Typography.Text>
                </Tooltip>
              ) : null}
              {teamClosed ? (
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  团队已关闭 — 成员仍可设「禁止」以便团队重开后继续限制
                </Typography.Text>
              ) : null}
            </div>
          )
        },
      },
    ],
    [
      baseline,
      canManageTeamSwitch,
      draft,
      memberEffective,
      onDecisionChange,
      onSelectNode,
      onTeamSwitchChange,
      readOnly,
      selectedNodeId,
      teamSwitchSavingKey,
      workspaceEffective,
    ],
  )

  return (
    <div className="amx-split" data-testid="member-auth-matrix">
      <div className="amx-split-nodes">
        <Table
          size="small"
          rowKey={(r) => r.node.subject_id}
          loading={loading}
          pagination={false}
          columns={columns}
          dataSource={rows}
          rowClassName={(r) => (r.node.subject_id === selectedNodeId ? 'amx-row-selected' : '')}
          onRow={(r) => ({ onClick: () => onSelectNode(r.node.subject_id) })}
          scroll={{ y: 420 }}
        />
      </div>
      <div className="amx-split-channels">
        <div className="amx-channel-panel-head">
          {selectedRow
            ? `通道：${selectedRow.node.display_name || selectedRow.node.subject_id}`
            : '选择左侧节点查看 Provider 通道'}
        </div>
        {selectedRow ? (
          <ChannelPanel
            row={selectedRow}
            draft={draft}
            baseline={baseline}
            workspaceEffective={workspaceEffective}
            memberEffective={memberEffective}
            onDecisionChange={onDecisionChange}
            canManageTeamSwitch={canManageTeamSwitch}
            onTeamSwitchChange={onTeamSwitchChange}
            teamSwitchSavingKey={teamSwitchSavingKey}
            readOnly={readOnly}
          />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="点击左侧功能节点" />
        )}
      </div>
    </div>
  )
}
